// src/components/views/FloorplanReviewView.tsx
// Phase D.2 Big-Bang rewrite: Tabs + Version Dropdown + Feintuning Pane
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { RefreshCw, Pencil, ArrowRight, ArrowLeft, Coins, Loader2, Maximize2 } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { FloorplanStepIndicator } from '@/components/FloorplanStepIndicator';
import { FloorplanVersionDropdown } from '@/components/floorplan/FloorplanVersionDropdown';
import { FloorplanFeintuningPane, type FloorplanSendOptions } from '@/components/floorplan/FloorplanFeintuningPane';
import { FloorplanVariantPickerModal } from '@/components/floorplan/FloorplanVariantPickerModal';
import { Lightbox } from '@/components/ui/Lightbox';
import { useFloorplanFeintuningThread } from '@/hooks/useFloorplanFeintuningThread';
import { fetchVersionsWithMessages, setActivePointer } from '@/services/floorplanService';
import { getImageUrl } from '@/lib/supabase';
import type { FloorplanState, BoxCoordinates, Project } from '@/types';

interface FloorplanReviewViewProps {
  projectId: string;
  currentProject: Project;
  floorplanState: FloorplanState;
  onFloorplanStateUpdate: (updates: Partial<FloorplanState>) => void;
  onFreshGeneration2D: () => void | Promise<void>;
  onNavigateTo3D: () => void;
  onBack: () => void;
  userId: string;
  credits: number;
  floorplanUnits: number;
}

export const FloorplanReviewView: React.FC<FloorplanReviewViewProps> = ({
  projectId,
  floorplanState,
  onFloorplanStateUpdate,
  onFreshGeneration2D,
  onNavigateTo3D,
  onBack,
  userId,
  floorplanUnits,
}) => {
  const [activeTab, setActiveTab] = useState<'result' | 'feintuning'>('result');
  const [variantPickerOpen, setVariantPickerOpen] = useState(false);
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);

  // Refresh versions on mount (defensive — parent may or may not have called fetch)
  useEffect(() => {
    if (!floorplanState.imageDbId) return;
    fetchVersionsWithMessages(floorplanState.imageDbId)
      .then(({ versions, messagesByVersion }) => {
        onFloorplanStateUpdate({ versions, messagesByVersion });
      })
      .catch(console.warn);
    // Only run on imageDbId change; onFloorplanStateUpdate is stable from parent
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [floorplanState.imageDbId]);

  const activeVersion = useMemo(
    () => floorplanState.versions.find((v) => v.id === floorplanState.activeVersionId) ?? null,
    [floorplanState.versions, floorplanState.activeVersionId]
  );

  const activeMessages = useMemo(
    () => (activeVersion ? floorplanState.messagesByVersion[activeVersion.id] ?? [] : []),
    [activeVersion, floorplanState.messagesByVersion]
  );

  const activeStatePath = useMemo(() => {
    if (!activeVersion) return floorplanState.generated2dPath ?? null;
    if (floorplanState.activeMessageNumber === null) return activeVersion.initialStoragePath;
    // Accept any message with a non-null resultingStoragePath, regardless of
    // status — Phase 2 introduced `awaiting_user_action` and could leave a
    // success message in a transient non-success state momentarily. Strict
    // status='success' here caused fallback to V<N>.initialStoragePath even
    // though the user-visible canvas + chat showed the variant active.
    const msg = activeMessages.find(
      (m) => m.messageNumber === floorplanState.activeMessageNumber && !!m.resultingStoragePath,
    );
    return msg?.resultingStoragePath ?? activeVersion.initialStoragePath;
  }, [activeVersion, floorplanState.activeMessageNumber, floorplanState.generated2dPath, activeMessages]);

  // Cache-buster: the storage path `v{N}/msg{M}.png` gets overwritten in-place
  // when a hard-reset + new edit reuses the same messageNumber slot. Browser
  // caches public URLs by URL key, so same-path overwrites look unchanged.
  // Append the DB row's UUID (message id, or version id for Ausgangs-Stand)
  // as a stable per-generation cache-buster — changes when the row is new.
  const activeStateUrl = useMemo(() => {
    if (!activeStatePath) return null;
    const baseUrl = getImageUrl(activeStatePath);
    if (floorplanState.activeMessageNumber !== null) {
      const msg = activeMessages.find(
        (m) => m.messageNumber === floorplanState.activeMessageNumber,
      );
      if (msg?.id) return `${baseUrl}?v=${msg.id}`;
    }
    if (activeVersion?.id) return `${baseUrl}?v=${activeVersion.id}`;
    return baseUrl;
  }, [activeStatePath, floorplanState.activeMessageNumber, activeMessages, activeVersion]);

  // The uploaded technical floor plan — shown as a reference next to the result
  // so agents can compare the generated plan against the original layout.
  const originalUrl = useMemo(() => {
    const path = floorplanState.sourceStoragePath ?? floorplanState.originalStoragePath;
    return path ? getImageUrl(path) : null;
  }, [floorplanState.sourceStoragePath, floorplanState.originalStoragePath]);

  const thread = useFloorplanFeintuningThread({
    imageId: floorplanState.imageDbId ?? '',
    activeVersion,
    initialMessages: activeMessages,
    currentStatePath: activeStatePath,
    sourceStoragePath: floorplanState.sourceStoragePath ?? floorplanState.originalStoragePath ?? '',
    projectId,
    userId,
    onResult: useCallback(() => {
      // Refresh versions after successful send
      if (!floorplanState.imageDbId) return;
      fetchVersionsWithMessages(floorplanState.imageDbId)
        .then(({ versions, messagesByVersion }) => {
          const currentVersionMessages = activeVersion ? messagesByVersion[activeVersion.id] ?? [] : [];
          const lastMsg = [...currentVersionMessages].reverse().find((m) => m.status === 'success');
          onFloorplanStateUpdate({
            versions,
            messagesByVersion,
            activeMessageNumber: lastMsg?.messageNumber ?? null,
          });
        })
        .catch(console.warn);
    }, [floorplanState.imageDbId, activeVersion, onFloorplanStateUpdate]),
  });

  const handleSelectVersion = useCallback(
    async (versionId: string, messageNumber: number | null) => {
      if (!floorplanState.imageDbId) return;
      await setActivePointer(floorplanState.imageDbId, versionId, messageNumber);
      onFloorplanStateUpdate({
        activeVersionId: versionId,
        activeMessageNumber: messageNumber,
      });
    },
    [floorplanState.imageDbId, onFloorplanStateUpdate]
  );

  const isGenerating2D = floorplanState.is2dGenerating ?? false;

  const handleNewVersion = useCallback(async () => {
    if (isGenerating2D) return; // guard against double-click during the ~1-3 min gen
    const confirmed = window.confirm(
      'Eine neue Version wird erstellt. Dein bisheriges Feintuning bleibt als vorherige Version erhalten. Fortfahren?'
    );
    if (!confirmed) return;
    await onFreshGeneration2D();
  }, [onFreshGeneration2D, isGenerating2D]);

  const handleSend = useCallback(
    async (prompt: string, box: BoxCoordinates | null, opts?: FloorplanSendOptions) => {
      if (!activeVersion || !floorplanState.imageDbId) return;

      // Hard-reset: if the user has rewound (pointer ≠ tail of chain), subsequent
      // messages get discarded so the new edit cleanly branches off the selected
      // state. Two rewind cases: activeMessageNumber === null (Ausgangs-Stand)
      // or a specific earlier messageNumber.
      const lastSuccessMsgNum =
        [...activeMessages].reverse().find((m) => m.status === 'success')?.messageNumber ?? null;
      const activeNum = floorplanState.activeMessageNumber;
      const isRewound =
        lastSuccessMsgNum !== null &&
        (activeNum === null || activeNum < lastSuccessMsgNum);

      if (isRewound) {
        const keepUpTo = activeNum ?? 0;
        const confirmed = window.confirm(
          activeNum === null
            ? 'Alle bisherigen Nachrichten dieser Version werden verworfen. Fortfahren?'
            : `Nachrichten nach Nachricht ${activeNum} dieser Version werden verworfen. Fortfahren?`
        );
        if (!confirmed) return;
        await thread.hardReset(keepUpTo);
      }

      try {
        await thread.sendMessage({ prompt, box, finalPromptOverride: opts?.finalPromptOverride });
      } catch (err) {
        // Error already handled inside the hook; nothing to do here
        console.debug('[FloorplanReviewView] sendMessage rejected:', err);
      }
    },
    [activeVersion, floorplanState.imageDbId, floorplanState.activeMessageNumber, activeMessages, activeStatePath, thread]
  );

  const handleRetryFailed = useCallback(
    async (messageId: string) => {
      // Retry = new INSERT + new OpenAI call (not retry of old message).
      // The failed message stays in the thread as a record; we re-send the
      // same prompt + box so the user doesn't have to retype anything
      // (especially important for transient OpenAI 5xx errors).
      const failedMsg = activeMessages.find((m) => m.id === messageId);
      if (!failedMsg) {
        console.warn('[FloorplanReviewView] retry: failed message not found', messageId);
        return;
      }
      if (!failedMsg.userPrompt) {
        console.warn('[FloorplanReviewView] retry: failed message has no userPrompt', messageId);
        return;
      }
      try {
        await thread.sendMessage({
          prompt: failedMsg.userPrompt,
          box: failedMsg.boxCoordinates ?? null,
        });
      } catch (err) {
        // Error already handled inside the hook; nothing to do here
        console.debug('[FloorplanReviewView] retry: sendMessage rejected', err);
      }
    },
    [activeMessages, thread]
  );

  const handleSelectMessage = useCallback(
    (messageNumber: number) => {
      if (!activeVersion) return;
      handleSelectVersion(activeVersion.id, messageNumber);
    },
    [activeVersion, handleSelectVersion]
  );

  const handleRebuildTriggered = useCallback(() => {
    if (!floorplanState.imageDbId) return;
    fetchVersionsWithMessages(floorplanState.imageDbId)
      .then(({ versions, messagesByVersion }) => {
        onFloorplanStateUpdate({ versions, messagesByVersion });
      })
      .catch(console.warn);
  }, [floorplanState.imageDbId, onFloorplanStateUpdate]);

  // Successful edits within the active version (skip the implicit Ausgangs-Stand).
  // Used to decide whether the variant-picker modal is worth showing.
  const successfulEditCount = useMemo(
    () =>
      activeMessages.filter(
        (m) => m.status === 'success' && !!m.resultingStoragePath,
      ).length,
    [activeMessages],
  );

  const navigateForwardWithPath = useCallback(
    (path: string) => {
      // Only update local state when the chosen variant differs from what
      // floorplanState already carries — otherwise the BrandingView/3D view
      // would see a no-op state churn.
      if (path !== floorplanState.generated2dPath) {
        onFloorplanStateUpdate({
          generated2dPath: path,
          generated2dUrl: getImageUrl(path),
        });
      }
      onNavigateTo3D();
    },
    [floorplanState.generated2dPath, onFloorplanStateUpdate, onNavigateTo3D],
  );

  const handleClickNext = useCallback(() => {
    // No edits beyond the initial -> nothing to choose, navigate directly.
    if (!activeVersion || successfulEditCount === 0) {
      const path = activeVersion?.initialStoragePath ?? floorplanState.generated2dPath;
      if (path) {
        navigateForwardWithPath(path);
      } else {
        onNavigateTo3D();
      }
      return;
    }
    setVariantPickerOpen(true);
  }, [
    activeVersion,
    successfulEditCount,
    floorplanState.generated2dPath,
    navigateForwardWithPath,
    onNavigateTo3D,
  ]);

  const handleVariantConfirm = useCallback(
    (path: string) => {
      setVariantPickerOpen(false);
      navigateForwardWithPath(path);
    },
    [navigateForwardWithPath],
  );

  return (
    <div className="flex flex-col h-full bg-background">
      {/* Header: only back button, no border (was causing a thin line above steps) */}
      <header className="flex items-center px-6 py-3">
        <Button variant="ghost" size="sm" onClick={onBack} className="gap-1">
          <ArrowLeft className="size-4" />
          Zurück zum Editor
        </Button>
      </header>

      <FloorplanStepIndicator currentStep="review" />

      {/* Version dropdown + credits below the step indicator */}
      <div className="flex items-center justify-end gap-2 px-6 pt-3 pb-1">
        <FloorplanVersionDropdown
          versions={floorplanState.versions}
          messagesByVersion={floorplanState.messagesByVersion}
          activeVersionId={floorplanState.activeVersionId}
          activeMessageNumber={floorplanState.activeMessageNumber}
          onSelectVersion={handleSelectVersion}
          onNewVersion={handleNewVersion}
          newVersionPrice={0}
        />
        <Badge className="bg-primary/10 text-primary border-primary/30 gap-1 px-3 py-1 text-xs">
          <Coins className="size-3 stroke-[1.5]" />
          {floorplanUnits} Grundriss{floorplanUnits !== 1 ? 'e' : ''}
        </Badge>
      </div>

      {isGenerating2D && (
        <div className="mx-6 mt-2 mb-1 rounded-lg border border-primary/30 bg-primary/5 px-4 py-3 flex items-center gap-3">
          <Loader2 className="size-4 animate-spin text-primary shrink-0" />
          <div className="text-sm">
            <span className="font-medium text-primary">Neue Version wird generiert…</span>
            <span className="text-muted-foreground ml-2">
              Kann bis zu 3 Minuten dauern. Du kannst währenddessen die aktuelle Version weiter bearbeiten.
            </span>
          </div>
        </div>
      )}

      {/* Tabs */}
      <Tabs
        value={activeTab}
        onValueChange={(v) => setActiveTab(v as 'result' | 'feintuning')}
        className="flex-1 flex flex-col min-h-0 px-6 pt-4"
      >
        <TabsList className="w-fit">
          <TabsTrigger value="result">Ergebnis</TabsTrigger>
          <TabsTrigger value="feintuning">Feintuning</TabsTrigger>
        </TabsList>

        <TabsContent value="result" className="flex-1 min-h-0 mt-3 data-[state=active]:flex flex-col">
          {activeStateUrl ? (
            <div className="flex gap-4 h-full min-h-0">
              <div className="flex-[2] bg-white border border-border rounded-lg flex items-center justify-center p-4 overflow-hidden shadow-sm">
                <img
                  src={activeStateUrl}
                  alt="Aktueller Grundriss"
                  className="max-w-full max-h-full object-contain select-none"
                  draggable={false}
                />
              </div>
              <div className="flex-1 flex flex-col gap-3 min-h-0">
                {originalUrl && (
                  <button
                    type="button"
                    onClick={() => setLightboxUrl(originalUrl)}
                    className="group block w-full text-left cursor-zoom-in rounded-lg"
                    title="Zum Vergrößern klicken"
                  >
                    <Card className="shadow-sm overflow-hidden transition-colors group-hover:border-primary/50">
                      <CardContent className="p-3 flex flex-col gap-2">
                        <div className="flex items-center justify-between">
                          <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                            Ursprungs-Grundriss
                          </span>
                          <Maximize2 className="size-3.5 text-muted-foreground/60 transition-colors group-hover:text-primary" />
                        </div>
                        <div className="relative aspect-[4/3] w-full rounded-md border border-border bg-white overflow-hidden">
                          <img
                            src={originalUrl}
                            alt="Ursprünglicher technischer Grundriss"
                            className="absolute inset-0 h-full w-full object-contain p-2 select-none"
                            draggable={false}
                          />
                          <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-foreground/0 opacity-0 transition-all duration-200 group-hover:bg-foreground/40 group-hover:opacity-100">
                            <span className="flex items-center gap-1.5 rounded-full bg-white/95 px-3 py-1 text-xs font-medium text-foreground shadow-sm">
                              <Maximize2 className="size-3.5" />
                              Vergrößern
                            </span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </button>
                )}
                <Card className="border-2 border-primary bg-card shadow-sm">
                  <CardContent className="p-4 flex flex-col gap-2">
                    <div className="flex items-center gap-2 font-semibold text-primary">
                      <RefreshCw className="size-4 stroke-[1.5]" />
                      Komplett neu generieren
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      Nicht zufrieden? Erzeuge einen völlig neuen Grundriss aus dem Original.
                    </p>
                    <Button
                      onClick={handleNewVersion}
                      disabled={isGenerating2D}
                      className="bg-primary hover:bg-primary/90 text-white text-sm"
                    >
                      {isGenerating2D ? (
                        <>
                          <Loader2 className="size-4 mr-2 animate-spin" />
                          Wird generiert…
                        </>
                      ) : (
                        <>Neu generieren (kostenlos)</>
                      )}
                    </Button>
                  </CardContent>
                </Card>

                <Card className="shadow-sm">
                  <CardContent className="p-4 flex flex-col gap-2">
                    <div className="flex items-center gap-2 font-semibold">
                      <Pencil className="size-4 stroke-[1.5]" />
                      Nur Details ändern
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      Nachbessern ist kostenlos – zeichne einen Bereich und beschreibe einfach, was geändert werden soll.
                    </p>
                    <Button variant="outline" onClick={() => setActiveTab('feintuning')} className="gap-1">
                      Zu Feintuning
                      <ArrowRight className="size-4" />
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
              Kein Grundriss vorhanden. Bitte zuerst 2D generieren.
            </div>
          )}
        </TabsContent>

        <TabsContent value="feintuning" className="flex-1 min-h-0 mt-3 data-[state=active]:flex flex-col">
          {activeStateUrl && activeVersion ? (
            <FloorplanFeintuningPane
              currentStateUrl={activeStateUrl}
              messages={thread.messages}
              activeMessageNumber={floorplanState.activeMessageNumber}
              isGenerating={thread.isGenerating}
              onSend={handleSend}
              onSelectMessage={handleSelectMessage}
              onRetryFailed={handleRetryFailed}
              imageId={floorplanState.imageDbId ?? ''}
              versionId={activeVersion.id}
              currentStatePath={activeStatePath ?? ''}
              projectId={projectId}
              userId={userId}
              sourceStoragePath={floorplanState.sourceStoragePath ?? floorplanState.originalStoragePath ?? ''}
              activeVersionNumber={activeVersion.versionNumber}
              rebuildSuggestion={thread.rebuildSuggestion}
              uncertainHint={thread.uncertainHint}
              dismissUncertain={thread.dismissUncertain}
              dismissRebuildSuggestion={thread.dismissRebuildSuggestion}
              forceEditRetry={thread.forceEditRetry}
              onRebuildTriggered={handleRebuildTriggered}
            />
          ) : (
            <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
              Kein aktiver Grundriss. Wechsle zu „Ergebnis" und generiere zuerst 2D.
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Footer */}
      <footer className="flex items-center justify-between px-6 py-3 border-t border-border">
        {activeTab === 'feintuning' ? (
          <Button variant="outline" onClick={() => setActiveTab('result')} className="gap-1">
            <ArrowLeft className="size-4" />
            Zurück zu Ergebnis
          </Button>
        ) : (
          <div />
        )}
        <Button
          onClick={handleClickNext}
          className="bg-foreground text-background hover:bg-foreground/90 gap-1 ml-auto"
        >
          Weiter: 3D
          <ArrowRight className="size-4" />
        </Button>
      </footer>

      <FloorplanVariantPickerModal
        open={variantPickerOpen}
        version={activeVersion}
        messages={activeMessages}
        activeMessageNumber={floorplanState.activeMessageNumber}
        onCancel={() => setVariantPickerOpen(false)}
        onConfirm={handleVariantConfirm}
      />

      {lightboxUrl && (
        <Lightbox url={lightboxUrl} onClose={() => setLightboxUrl(null)} />
      )}
    </div>
  );
};
